// ─────────────────────────────────────────────────────────────
// ceramicCalculator.js
// Universal quantity & box calculator for all product unit types
// Handles: m² (area tiles), linear_meter (skirting), pcs (other)
// ─────────────────────────────────────────────────────────────


// ── PRECISION HELPER ─────────────────────────────────────────
/**
 * Rounds a number to N decimal places to eliminate floating point dust.
 *
 * WHY THIS EXISTS:
 * JavaScript stores decimals in binary (IEEE 754), which cannot represent
 * most decimal fractions exactly. So:
 *   0.3 * 0.6 = 0.17999999999999999   (not 0.18)
 *   35.1 / 0.18 = 194.99999999999997  (not 195)
 *
 * This causes Math.floor(194.9999...) = 194 instead of 195.
 * Rounding to 10 decimal places collapses the dust:
 *   round(194.9999999999997, 10) = 195.0 ✅
 *
 * @param {number} n          — the number to round
 * @param {number} precision  — decimal places to keep (default 10)
 * @returns {number}
 */
const round = (n, precision = 10) =>
  Math.round(n * Math.pow(10, precision)) / Math.pow(10, precision);


// ── SAFE DIVIDE → PIECES ─────────────────────────────────────
/**
 * Converts a native-unit quantity (m² or meters) to a piece count,
 * safely avoiding floating point errors.
 *
 * @param {number} size        — quantity in native unit (e.g. 35.1 m²)
 * @param {number} sizePerItem — size of one piece (e.g. 0.18 m² per tile)
 * @returns {number}           — piece count with float dust removed
 *
 * Example:
 *   toPieces(35.1, 0.18)
 *   → 35.1 / 0.18 = 194.9999999999997 (raw)
 *   → round(194.9999..., 10) = 195.0  (clean)
 */
const toPieces = (size, sizePerItem) => {
  if (!sizePerItem || sizePerItem <= 0) return size;
  return round(size / sizePerItem);
};


// ── APPLY WASTAGE ─────────────────────────────────────────────
/**
 * Increases a target quantity by a wastage percentage.
 * Used when the customer needs extra material for cuts and breakage.
 *
 * @param {number} target  — customer's required quantity in native unit
 *                           e.g. 35.1 m² for a room
 * @param {number} wastage — percentage to add (0–100), e.g. 10 for 10%
 * @returns {number}       — adjusted quantity
 *
 * Example:
 *   applyWastage(35.1, 10)
 *   → 35.1 * (1 + 10/100)
 *   → 35.1 * 1.1
 *   → 38.61 m²
 */
export function applyWastage(target, wastage = 0) {
  if (!wastage || wastage <= 0) return target; // no wastage — return unchanged
  return target * (1 + wastage / 100);
}


// ── CALC BUY OPTIONS ──────────────────────────────────────────
/**
 * The main calculation function.
 * Given a target quantity, returns two buying options:
 *   - up:   round UP  to nearest whole piece (recommended — no shortage)
 *   - down: round DOWN to nearest whole piece (cheaper — slight shortage risk)
 *
 * When target divides evenly into whole pieces (e.g. 35.1 ÷ 0.18 = 195 exactly),
 * both up and down are the same, so we return only one option (down: null).
 *
 * @param {number} target   — customer's required quantity, AFTER wastage is applied
 *                            in native unit: m², meters, or pcs
 *                            e.g. 35.1 (m²) or 10.5 (meters) or 6 (pcs)
 *
 * @param {object} product  — the product document from the database, must contain:
 *                            product.sizePerItem  — m²/piece or meters/piece
 *                            product.itemsPerBox  — pieces per carton
 *                            product.sizePerBox   — m²/box or meters/box
 *
 * @param {string} unit     — the measurement unit of this product's category:
 *                            "m2"           → area (floor/wall tiles)
 *                            "linear_meter" → length (skirting boards)
 *                            "pcs"          → plain count (frames, sinks etc.)
 *
 * @returns {{ up, down } | null}
 *   Returns null if target is 0 or negative.
 *   Each option is a buildOption() result — see below.
 */
export function calcBuyOptions(target, product, unit) {
  const { sizePerItem, itemsPerBox } = product;

  // Nothing to calculate
  if (!target || target <= 0) return null;

  // ── PCS-ONLY PATH ─────────────────────────────────────────
  // Products sold by count with no size conversion (frames, WC, sinks)
  // OR products that have no sizePerItem set
  if (unit === "pcs" || !sizePerItem || sizePerItem <= 0) {

    const exact      = round(target);        // clean any float dust first
    const piecesUp   = Math.ceil(exact);     // e.g. 6.0 → 6, 6.3 → 7
    const piecesDown = Math.floor(exact);    // e.g. 6.0 → 6, 6.3 → 6

    return {
      up:   buildOption(piecesUp, piecesUp, unit, itemsPerBox),
      // Only show down if it's actually different from up
      down: piecesDown > 0 && piecesDown !== piecesUp
        ? buildOption(piecesDown, piecesDown, unit, itemsPerBox)
        : null,
    };
  }

  // ── M² OR LINEAR_METER PATH ───────────────────────────────
  // Convert the target size → pieces using float-safe division
  // e.g. 35.1 m² ÷ 0.18 m²/piece = 195 pieces (safely)
  const exactPieces = toPieces(target, sizePerItem);

  const piecesUp    = Math.ceil(exactPieces);   // round up  → no shortage
  const piecesDown  = Math.floor(exactPieces);  // round down → possible shortage

  // ── EXACT MATCH — both options are identical ───────────────
  // e.g. 35.1 ÷ 0.18 = 195.0 exactly → no point showing two identical buttons
  if (piecesUp === piecesDown) {
    return {
      up:   buildOption(
              piecesUp,
              round(piecesUp * sizePerItem), // total size for those pieces
              unit,
              itemsPerBox
            ),
      down: null, // ✅ single option — no ambiguity
    };
  }

  // ── TWO DIFFERENT OPTIONS ─────────────────────────────────
  // e.g. 35.5 ÷ 0.18 = 197.22... → up=198 pcs, down=197 pcs
  return {
    up:   buildOption(
            piecesUp,
            round(piecesUp * sizePerItem),   // e.g. 198 × 0.18 = 35.64 m²
            unit,
            itemsPerBox
          ),
    down: buildOption(
            piecesDown,
            round(piecesDown * sizePerItem), // e.g. 197 × 0.18 = 35.46 m²
            unit,
            itemsPerBox
          ),
  };
}


// ── BUILD OPTION ──────────────────────────────────────────────
/**
 * Assembles one buying option — used internally by calcBuyOptions.
 * Takes a piece count and converts it into a full human-readable result.
 *
 * @param {number} pieces      — number of pieces for this option
 *                               e.g. 195
 *
 * @param {number} totalSize   — total area or length for these pieces
 *                               in native unit (m² or meters)
 *                               e.g. 195 × 0.18 = 35.1 m²
 *                               For pcs products, this equals pieces.
 *
 * @param {string} unit        — "m2" | "linear_meter" | "pcs"
 *
 * @param {number} itemsPerBox — how many pieces fit in one carton
 *                               e.g. 10 pieces per box
 *                               null if product is not sold in boxes
 *
 * @returns {object}
 *   pieces       {number}  — piece count             e.g. 195
 *   totalSize    {number}  — size in native unit      e.g. 35.1
 *   boxes        {number}  — full cartons needed      e.g. 19
 *   extraPieces  {number}  — leftover pieces          e.g. 5
 *   sizeLabel    {string}  — display string           e.g. "35.1 m²"
 *   boxLabel     {string}  — box breakdown string     e.g. "19 boxes + 5 pcs"
 *   quantityValue {number} — value to store/display   e.g. 35.1 (m²) or 195 (pcs)
 */
function buildOption(pieces, totalSize, unit, itemsPerBox) {

  // ── Box breakdown ─────────────────────────────────────────
  // How many full boxes + how many loose pieces left over
  // e.g. 195 pieces ÷ 10 per box = 19 boxes remainder 5 pieces
  const boxes       = itemsPerBox ? Math.floor(pieces / itemsPerBox) : null;
  const extraPieces = itemsPerBox ? pieces % itemsPerBox             : null;

  // ── Size label ────────────────────────────────────────────
  // What the user sees as the main quantity
  // m²         → "35.1 m²"    (4 decimal places max)
  // linear_meter → "10.2 m"   (3 decimal places max)
  // pcs        → "195 pcs"    (whole number, no decimals)
  let sizeLabel;
  if      (unit === "m2")           sizeLabel = `${round(totalSize, 4)} m²`;
  else if (unit === "linear_meter") sizeLabel = `${round(totalSize, 3)} m`;
  else                              sizeLabel = `${pieces} pcs`;

  // ── Box label ─────────────────────────────────────────────
  // Human-readable carton breakdown shown below the main quantity
  // "19 boxes + 5 pcs"  — has full boxes and leftover
  // "19 boxes"          — exactly full boxes, no leftover
  // "5 pcs"             — less than one full box
  // null                — product has no box info
  let boxLabel = null;
  if (itemsPerBox) {
    if      (boxes > 0 && extraPieces > 0) boxLabel = `${boxes} box${boxes > 1 ? 'es' : ''} + ${extraPieces} pcs`;
    else if (boxes > 0)                    boxLabel = `${boxes} box${boxes > 1 ? 'es' : ''}`;
    else                                   boxLabel = `${extraPieces} pcs`;
  }

  return {
    pieces,                              // e.g. 195
    totalSize:     round(totalSize, 4),  // e.g. 35.1
    boxes,                               // e.g. 19
    extraPieces,                         // e.g. 5
    sizeLabel,                           // e.g. "35.1 m²"
    boxLabel,                            // e.g. "19 boxes + 5 pcs"
    // quantityValue is what gets stored in the sale item quantity field
    // For m²/linear products: store the size (35.1 m²) not the piece count
    // For pcs products:       store the piece count (195)
    quantityValue: unit === "pcs" ? pieces : round(totalSize, 4),
  };
}


// ── FORMAT PIECE SUMMARY ──────────────────────────────────────
/**
 * Converts a raw piece count into a full human-readable summary string.
 * Used in the sales table Items column and receipt to show what was sold.
 *
 * @param {number} pieces  — total pieces sold, e.g. 195
 *
 * @param {object} product — product doc with:
 *                           product.sizePerItem  — e.g. 0.18 m²/piece
 *                           product.itemsPerBox  — e.g. 10 pieces/box
 *
 * @param {string} unit    — "m2" | "linear_meter" | "pcs"
 *
 * @returns {string}
 *
 * Examples:
 *   195 pcs, m², sizePerItem=0.18, itemsPerBox=10
 *   → "195 pcs (19 boxes + 5 pcs = 35.10 m²)"
 *
 *   20 pcs, m², sizePerItem=0.18, itemsPerBox=10
 *   → "20 pcs (2 boxes = 3.60 m²)"
 *
 *   5 pcs, m², sizePerItem=0.18, no itemsPerBox
 *   → "5 pcs = 0.90 m²"
 *
 *   6 pcs, pcs, no sizePerItem
 *   → "6 pcs"
 */
export function formatPieceSummary(pieces, product, unit) {
  if (!pieces) return `${pieces} pcs`;

  const { sizePerItem, itemsPerBox } = product;

  // Total area or length for all pieces
  // e.g. 195 pieces × 0.18 m²/piece = 35.1 m²
  const totalSize   = sizePerItem ? round(pieces * sizePerItem) : null;

  // Box breakdown
  const boxes       = itemsPerBox ? Math.floor(pieces / itemsPerBox) : null;
  const extraPieces = itemsPerBox ? pieces % itemsPerBox             : null;

  // Size suffix appended at the end
  // e.g. " = 35.10 m²" or " = 10.20 m" or "" for pcs
  let sizeStr = "";
  if (totalSize) {
    sizeStr = unit === "m2"
      ? ` = ${totalSize.toFixed(2)} m²`
      : unit === "linear_meter"
      ? ` = ${totalSize.toFixed(2)} m`
      : "";
  }

  // Build the final string based on what data is available
  if (!itemsPerBox)      return `${pieces} pcs${sizeStr}`;
  if (boxes === 0)       return `${pieces} pcs${sizeStr}`;
  if (extraPieces === 0) return `${pieces} pcs (${boxes} box${boxes > 1 ? 'es' : ''}${sizeStr})`;
  return `${pieces} pcs (${boxes} box${boxes > 1 ? 'es' : ''} + ${extraPieces} pcs${sizeStr})`;
}